package no.vareflow.app.domain

/**
 * Who may see and do what. Pure functions: everything they need is passed in, nothing is read
 * from storage or the network, so each rule can be unit tested on its own.
 *
 * These checks drive the app's UI. In Firebase mode the same rules are repeated server-side in
 * `firestore.rules`; the UI checks are not a security boundary on their own.
 */
object AccessRules {

    /** ADMIN implicitly holds every permission. */
    fun hasPermission(user: UserAccount, permission: Permission): Boolean =
        user.role == Role.ADMIN || permission in user.permissions

    fun canViewCategory(user: UserAccount, category: String): Boolean =
        user.role == Role.ADMIN ||
            hasPermission(user, Permission.INVENTORY_VIEW_OTHER) ||
            category in user.allowedCategories

    fun canAccessAdmin(user: UserAccount): Boolean =
        user.role == Role.ADMIN ||
            hasPermission(user, Permission.USER_MANAGE) ||
            hasPermission(user, Permission.AUDIT_VIEW) ||
            hasPermission(user, Permission.PRODUCT_REVIEW_QUALITY)

    private val productEditPermissions = listOf(
        Permission.PRODUCT_EDIT_NAME, Permission.PRODUCT_EDIT_NUMBER, Permission.PRODUCT_EDIT_CATEGORY,
        Permission.PRODUCT_EDIT_UNIT, Permission.PRODUCT_EDIT_VAT, Permission.PRODUCT_EDIT_PRICE,
        Permission.PRODUCT_EDIT_SUPPLIER, Permission.PRODUCT_EDIT_ALIASES, Permission.PRODUCT_EDIT_ACTIVE,
        Permission.PRODUCT_REVIEW_QUALITY
    )

    fun canEditAnyProductField(user: UserAccount): Boolean =
        user.role == Role.ADMIN || productEditPermissions.any { hasPermission(user, it) }

    /**
     * Categories shown to [user] in a monthly session. Admins and users allowed to see other
     * categories get all of them; everyone else only the categories assigned to them.
     *
     * @param orderedCategories all categories in display order
     * @param sessionAssignments the category assignments of that session
     */
    fun visibleCategories(
        orderedCategories: List<String>,
        sessionAssignments: List<CategoryAssignment>,
        user: UserAccount
    ): List<String> {
        if (user.role == Role.ADMIN || hasPermission(user, Permission.INVENTORY_VIEW_OTHER)) return orderedCategories
        return orderedCategories.filter { category ->
            sessionAssignments.firstOrNull { it.category == category }?.assigneeIds?.contains(user.id) == true
        }
    }

    /**
     * May [user] change counts in [assignment]'s category of [session]?
     * Never when the session is not ACTIVE or the category is DONE. Admins otherwise may;
     * other users need INVENTORY_COUNT_ASSIGNED and must be assigned to the category.
     */
    fun canEditCategory(session: InventorySession?, assignment: CategoryAssignment?, user: UserAccount): Boolean {
        if (session == null || !InventoryRules.countsAreEditable(session)) return false
        // No Ferdig lock since v0.8.36: old DONE statuses no longer block counting.
        // Admins may count every Hovedvaregruppe, also one added after the month was created (no assignment yet).
        if (user.role == Role.ADMIN) return true
        if (assignment == null) return false
        return hasPermission(user, Permission.INVENTORY_COUNT_ASSIGNED) && user.id in assignment.assigneeIds
    }

    /**
     * May [user] reopen a DONE category so it can be counted again? Only while the session is
     * ACTIVE, and only admins or users who may assign categories.
     */
    fun canReopenCategory(session: InventorySession?, assignment: CategoryAssignment?, user: UserAccount): Boolean {
        if (session == null || !InventoryRules.countsAreEditable(session)) return false
        if (assignment == null || assignment.status != CategoryWorkStatus.DONE) return false
        return hasPermission(user, Permission.INVENTORY_ASSIGN_CATEGORY)
    }
}
